import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { Button } from '@/components/ui/button'
import { DeliveryDetailsModal } from '@/components/admin/delivery-details-modal'
import { Toast } from '@/components/ui/toast'
import { BackButton } from '@/components/ui/back-button'
import {
  Search,
  Filter,
  ArrowLeft,
  Truck,
  User,
  Calendar,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  MapPin
} from 'lucide-react'

export function DeliveryPartnersPage() {
  const navigate = useNavigate()
  const [partners, setPartners] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState<'all' | 'pending' | 'active' | 'blocked'>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedPartner, setSelectedPartner] = useState<any>(null)
  const [showDetails, setShowDetails] = useState(false)
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [toastType, setToastType] = useState<'success' | 'error'>('success')

  useEffect(() => {
    fetchPartners()
  }, [filter, searchQuery])

  const fetchPartners = async () => {
    try {
      setLoading(true)
      setError(null)
      console.log('Fetching partners with filter:', filter)

      let query = supabase
        .from('delivery_partners')
        .select('*')
        .order('created_at', { ascending: false })

      // Only show pending applications and active/blocked partners
      if (filter === 'pending') {
        query = query.eq('status', 'pending')
      } else if (filter === 'active') {
        query = query.eq('status', 'active')
      } else if (filter === 'blocked') {
        query = query.eq('status', 'blocked')
      } else {
        // For 'all', show everything except rejected
        query = query.neq('status', 'rejected')
      }

      if (searchQuery) {
        query = query.or(`applicant_name.ilike.%${searchQuery}%,applicant_email.ilike.%${searchQuery}%,delivery_zone.ilike.%${searchQuery}%`)
      }

      const { data, error } = await query

      if (error) {
        console.error('Database error:', error)
        throw new Error('Failed to fetch delivery partners')
      }

      console.log('Fetched partners:', data)
      setPartners(data || [])
    } catch (err) {
      console.error('Error fetching delivery partners:', err)
      setError(err instanceof Error ? err.message : 'An error occurred while fetching delivery partners')
    } finally {
      setLoading(false)
    }
  }

  const handleAction = async (action: 'approve' | 'reject' | 'block' | 'unblock') => {
    if (!selectedPartner) return

    try {
      setLoading(true)
      const status = action === 'approve' ? 'active' : 
                    action === 'reject' ? 'rejected' :
                    action === 'block' ? 'blocked' : 'active'

      const { error: updateError } = await supabase
        .from('delivery_partners')
        .update({ status })
        .eq('id', selectedPartner.id)

      if (updateError) throw updateError

      // Update delivery partner profile status
      const { error: profileError } = await supabase
        .from('profiles')
        .update({ 
          status: status === 'active' ? 'active' : status === 'rejected' ? 'blocked' : status,
          role: status === 'active' ? 'delivery' : 'buyer'
        })
        .eq('id', selectedPartner.id)

      if (profileError) throw profileError

      // Create notification
      const { error: notificationError } = await supabase
        .from('notifications')
        .insert([{
          user_id: selectedPartner.id,
          title: `Demande Livreur ${status === 'active' ? 'Approuvée' : status === 'rejected' ? 'Refusée' : status === 'blocked' ? 'Bloquée' : 'Réactivée'}`,
          content: status === 'active' 
            ? 'Votre demande livreur a été approuvée ! Vous pouvez maintenant commencer à livrer.'
            : `Votre demande livreur a été ${status === 'rejected' ? 'refusée' : status === 'blocked' ? 'bloquée' : 'réactivée'}.`,
          type: 'system'
        }])

      if (notificationError) throw notificationError

      setToastMessage(`Livreur ${status === 'active' ? 'approuvé' : status === 'rejected' ? 'refusé' : status === 'blocked' ? 'bloqué' : 'réactivé'} avec succès`)
      setToastType('success')
      setShowToast(true)
      setShowDetails(false)
      fetchPartners()
    } catch (err) {
      console.error('Error updating delivery partner:', err)
      setToastMessage('Une erreur est survenue')
      setToastType('error')
      setShowToast(true)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      {showToast && (
        <Toast
          type={toastType}
          title={toastMessage}
          onClose={() => setShowToast(false)}
        />
      )}

      {showDetails && selectedPartner && (
        <DeliveryDetailsModal
          partner={selectedPartner}
          onClose={() => {
            setShowDetails(false)
            setSelectedPartner(null)
          }}
          onAction={handleAction}
        />
      )}

      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center">
            <BackButton to="/admin" label="Retour" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Gestion des Livreurs
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                Gérez les demandes et les comptes livreurs
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="p-6">
            <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center space-y-4 sm:space-y-0 sm:space-x-4 mb-8">
              <div className="flex-1 max-w-lg">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Rechercher un livreur..."
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary focus:border-primary sm:text-sm"
                  />
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value as typeof filter)}
                  className="block w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-primary focus:border-primary"
                >
                  <option value="all">Tous les statuts</option>
                  <option value="pending">En attente</option>
                  <option value="active">Actifs</option>
                  <option value="blocked">Bloqués</option>
                </select>
              </div>
            </div>

            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <AlertTriangle className="mx-auto h-12 w-12 text-red-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">Erreur</h3>
                <p className="mt-1 text-sm text-gray-500">{error}</p>
                <Button
                  variant="primary"
                  className="mt-4"
                  onClick={() => fetchPartners()}
                >
                  Réessayer
                </Button>
              </div>
            ) : partners.length === 0 ? (
              <div className="text-center py-12">
                <Truck className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun livreur</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Aucun livreur ne correspond à vos critères
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Livreur
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Véhicule
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Zone
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Statut
                      </th>
                      <th scope="col" className="relative px-6 py-3">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {partners.map((partner) => (
                      <tr key={partner.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="h-10 w-10 flex-shrink-0">
                              <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                                <User className="h-5 w-5 text-gray-500" />
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {partner.applicant_name}
                              </div>
                              <div className="text-sm text-gray-500">
                                {partner.applicant_email}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <Truck className="h-5 w-5 text-gray-400 mr-2" />
                            <span className="text-sm text-gray-900 capitalize">
                              {partner.vehicle_type === 'bike' ? 'Vélo' :
                               partner.vehicle_type === 'motorcycle' ? 'Moto' :
                               partner.vehicle_type === 'car' ? 'Voiture' :
                               partner.vehicle_type === 'van' ? 'Camionnette' : 'À pied'}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <MapPin className="h-5 w-5 text-gray-400 mr-2" />
                            <span className="text-sm text-gray-900">
                              {partner.delivery_zone}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div className="flex items-center">
                            <Calendar className="h-4 w-4 mr-1" />
                            {new Date(partner.created_at).toLocaleDateString()}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            partner.status === 'pending'
                              ? 'bg-yellow-100 text-yellow-800'
                              : partner.status === 'active'
                              ? 'bg-green-100 text-green-800'
                              : partner.status === 'rejected'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {partner.status === 'pending' && <Clock className="h-3 w-3 mr-1" />}
                            {partner.status === 'active' && <CheckCircle className="h-3 w-3 mr-1" />}
                            {partner.status === 'rejected' && <XCircle className="h-3 w-3 mr-1" />}
                            {partner.status === 'blocked' && <AlertTriangle className="h-3 w-3 mr-1" />}
                            {partner.status === 'pending' ? 'En attente' :
                             partner.status === 'active' ? 'Actif' :
                             partner.status === 'rejected' ? 'Refusé' :
                             partner.status === 'blocked' ? 'Bloqué' : partner.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedPartner(partner)
                              setShowDetails(true)
                            }}
                          >
                            Détails
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}